import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/authService';
import { log } from '@/utils/vueLogger.js';
import { CacheManager } from '@/utils/cacheManager.js';

class FileCacheManager extends CacheManager {
	constructor() {
		super('FileCache', 'files', {
			keyPath: 'id',
			maxAge: 7 * 24 * 60 * 60 * 1000,
			logCategory: 'File',
		});
	}

	async putFile(id, blob, mimeType) {
		if (!this.db) await this.initDB();

		return new Promise((resolve, reject) => {
			const transaction = this.db.transaction([this.storeName], 'readwrite');
			const store = transaction.objectStore(this.storeName);

			const fileData = {
				id: id,
				blob: blob,
				mimeType: mimeType,
				timestamp: Date.now(),
			};

			const request = store.put(fileData);
			request.onsuccess = () => resolve();
			request.onerror = () => reject(request.error);
		});
	}

	async getFile(id) {
		if (!this.db) await this.initDB();

		return new Promise((resolve, reject) => {
			const transaction = this.db.transaction([this.storeName], 'readonly');
			const store = transaction.objectStore(this.storeName);

			const request = store.get(id);
			request.onsuccess = () => {
				const result = request.result;
				resolve(result ? result.blob : null);
			};
			request.onerror = () => reject(request.error);
		});
	}
}

const fileCache = new FileCacheManager();
const urlCache = new Map();

export async function getFileUrl(fileId) {
	if (!fileId || fileId === '00000000-0000-0000-0000-000000000000' || fileId.length !== 36) return null;

	if (urlCache.has(fileId)) {
		return urlCache.get(fileId);
	}

	try {
		const cachedBlob = await fileCache.getFile(fileId);
		if (cachedBlob) {
			const objectUrl = URL.createObjectURL(cachedBlob);
			urlCache.set(fileId, objectUrl);
			return objectUrl;
		}

		if (iframeDataBridge.isOffline()) {
			console.log(`📴 Offline: File ${fileId} not found in cache`);
			urlCache.set(fileId, null);
			return null;
		}

		const apiUrl = getApiUrl(`Core/Files/${fileId}`);
		const response = await authenticatedFetch(apiUrl, {}, 'token');

		if (response.ok) {
			const blob = await response.blob();
			const mimeType = response.headers.get('content-type') || 'application/octet-stream';

			await fileCache.putFile(fileId, blob, mimeType);

			const objectUrl = URL.createObjectURL(blob);
			urlCache.set(fileId, objectUrl);

			return objectUrl;
		} else {
			urlCache.set(fileId, null);
			return null;
		}
	} catch (error) {
		console.error('Error loading file:', error);
		urlCache.set(fileId, null);
		return null;
	}
}

export async function getFileBlob(fileId) {
	try {
		const cachedBlob = await fileCache.getFile(fileId);
		if (cachedBlob) {
			return cachedBlob;
		}

		if (iframeDataBridge.isOffline()) {
			throw new Error(`Offline: File ${fileId} not available in cache`);
		}

		const apiUrl = getApiUrl(`Core/Files/${fileId}`);
		const response = await authenticatedFetch(apiUrl, {}, 'token');

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const blob = await response.blob();
		const mimeType = response.headers.get('content-type') || 'application/octet-stream';

		await fileCache.putFile(fileId, blob, mimeType);

		return blob;
	} catch (error) {
		log('error', 'File', `Error loading file: ${error.message}`);
		throw error;
	}
}

export async function getFileAsBase64(fileId) {
	try {
		const blob = await getFileBlob(fileId);
		if (!blob) return null;

		return await convertBlobToBase64(blob);
	} catch (error) {
		console.error('Error converting file to base64:', error);
		return null;
	}
}

function convertBlobToBase64(blob) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onloadend = () => resolve(reader.result);
		reader.onerror = reject;
		reader.readAsDataURL(blob);
	});
}

export async function getAccountImageUrl(fileId) {
	if (!fileId || fileId === '00000000-0000-0000-0000-000000000000' || fileId.length !== 36) return null;
	return await getFileUrl(fileId);
}

export async function clearFileCache(fileId = null) {
	if (fileId) {
		await fileCache.delete(fileId);
		urlCache.delete(fileId);
	} else {
		await fileCache.clearAll();
		for (const [key, url] of urlCache.entries()) {
			if (url && typeof url === 'string' && url.startsWith('blob:')) {
				URL.revokeObjectURL(url);
			}
		}
		urlCache.clear();
	}
}

export async function cleanOldCacheEntries(maxAgeInDays = 7) {
	await fileCache.cleanOld(maxAgeInDays);
}

export async function getCacheStats() {
	const stats = await fileCache.getStats();
	return {
		...stats,
		inMemoryUrls: urlCache.size,
	};
}

cleanOldCacheEntries();
